import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';

import {
  ClientInformationDrawerTab,
  useMarkFormResponseAsReviewed,
  useSetActiveClientInfoDrawerTab,
  useTrackIntakeFormReviewDuration,
} from '@grow-therapy-team/seedling-components';

import { ProviderDrawerState, drawerStateAtom } from '../state';
import { useGetPatientByShortId } from '../useGetPatientByShortId';
import { ClientInformationFormResponseDrawer } from './ClientInformationFormResponseDrawer';
import {
  formDataForSelectedClientAtom,
  intakeFormReviewSourceAtom,
  selectedFormTitleAtom,
  selectedGroupedFormAtom,
} from './state';
import { FormType, GroupedFormData } from './types';
import { useSelectedClientAppointmentIds } from './useSelectedClientAppointmentIds';

function useIntakeFormReviewTracking(
  selectedForms: GroupedFormData[] | undefined,
  formEntry: number,
  revealResults?: boolean,
) {
  const { markFormResponseAsReviewed } = useMarkFormResponseAsReviewed();
  const markedResponseIds = useRef<Set<string>>(new Set());

  useEffect(
    function markIntakeFormAsReviewedOnView() {
      const selectedForm = selectedForms?.[formEntry];
      const responseId = selectedForm?.formResponseId;
      if (!revealResults || selectedForm?.formType !== FormType.Intake || !responseId) return;
      if (markedResponseIds.current.has(responseId)) return;
      markedResponseIds.current.add(responseId);
      markFormResponseAsReviewed({ formResponseId: parseInt(responseId) });
    },
    [revealResults, formEntry, selectedForms, markFormResponseAsReviewed],
  );

  const source = useAtomValue(intakeFormReviewSourceAtom);
  const selectedForm = selectedForms?.[formEntry];
  const responseId = selectedForm?.formResponseId;

  // Duration comes from the shared seedlings hook so telehealth and csr-app report the
  // same thing: on-screen time, excluding stretches where the tab was hidden.
  useTrackIntakeFormReviewDuration({
    isActive: Boolean(revealResults) && selectedForm?.formType === FormType.Intake,
    formResponseId: responseId ? parseInt(responseId) : undefined,
    source,
  });
}

export function ClientInformationFormResponseDrawerWrapper() {
  const selectedFormTitle = useAtomValue(selectedFormTitleAtom);
  const selectedFormResponses = useAtomValue(selectedGroupedFormAtom);
  const formDataForSelectedClient = useAtomValue(formDataForSelectedClientAtom);
  const hasSingleSubmission = selectedFormResponses?.length === 1;
  const setDrawerState = useSetAtom(drawerStateAtom);
  const setIntakeFormReviewSource = useSetAtom(intakeFormReviewSourceAtom);
  const setSeedlingActiveTab = useSetActiveClientInfoDrawerTab();
  // null until the user toggles; falls back to auto-revealing a single submission once forms load
  const [userToggledRevealResults, setUserToggledRevealResults] = useState<boolean | null>(null);
  const revealResults = userToggledRevealResults ?? Boolean(hasSingleSubmission);
  const [formEntry, setFormEntry] = useState(0);
  const { appointmentShortId: selectedAppointmentShortId, patientShortId } = useSelectedClientAppointmentIds();
  const { data: patientChartData } = useGetPatientByShortId(selectedAppointmentShortId ? undefined : patientShortId);
  const appointmentShortId =
    selectedAppointmentShortId || patientChartData?.patientChart?.lastAppointment?.appointmentShortId || undefined;

  useIntakeFormReviewTracking(selectedFormResponses, formEntry, revealResults);

  const onClickBackButton = () => {
    if (revealResults && !hasSingleSubmission) {
      setUserToggledRevealResults(false);
    } else {
      setSeedlingActiveTab(ClientInformationDrawerTab.INFORMATION);
      setDrawerState(ProviderDrawerState.CLIENT_INFORMATION);
    }
  };

  const onClickForm = (i: number) => {
    // Picking a response off the list is its own entry point, even when a summary
    // click-through is what opened the drawer.
    setIntakeFormReviewSource('telehealth_forms_list');
    setFormEntry(i);
    setUserToggledRevealResults(true);
  };

  if (!selectedFormTitle) return;

  const isLoadingForms = !formDataForSelectedClient;

  if (isLoadingForms) {
    return (
      <ClientInformationFormResponseDrawer
        selectedFormTitle={selectedFormTitle}
        isLoading
        onClickBackButton={onClickBackButton}
      />
    );
  }

  if (!selectedFormResponses) return;

  return (
    <ClientInformationFormResponseDrawer
      selectedFormTitle={selectedFormTitle}
      revealResults={revealResults}
      selectedForms={selectedFormResponses}
      onClickBackButton={onClickBackButton}
      onClickForm={onClickForm}
      formEntry={formEntry}
      appointmentShortId={appointmentShortId}
    />
  );
}
